import React, { useState, useEffect, useRef } from "react";
import Config from "src/Constants/Config";

interface PulseConfig {
  enabled?: boolean;
  duration?: number; // in seconds, default 1.5
  size?: number; // max blur radius in pixels for the glow, default 10
  color?: string; // glow color, default "rgba(255, 255, 0, 0.5)"
}

interface ChatMessageAnimationProps {
  startAnimation?: boolean;
  initialDelay?: number;
  delayBetween?: number;
  baseImagePath?: string;
  imageExtension?: string;
  containerStyle?: React.CSSProperties;
  chatWidth?: string; // Relative width of chat container, e.g., "90%"
  verticalSpacing?: string; // Spacing between messages, e.g., "2%"
  initialBottomOffset?: string; // (Unused in this version)
  maxWidth?: string; // Max width for each message (relative), e.g., "var(--width-350)"
  // Use the length of this array to determine the number of images and their target scales.
  scaleArray?: number[];
  pulseConfig?: PulseConfig;
  transformPositionX?: string;
  transformPositionY?: string;
  // Props for scroll functionality
  enableScroll?: boolean; // Whether to enable scrolling (default: false)
  scrollThreshold?: number; // Number of messages before scrolling starts (default: 5)
}

const ChatMessageAnimation: React.FC<ChatMessageAnimationProps> = ({
  startAnimation = false,
  initialDelay = 500,
  delayBetween = 800,
  baseImagePath = `${Config.BASE_URL}/images/screen_4/right_`,
  imageExtension = "png",
  containerStyle = {},
  chatWidth = "90%",
  verticalSpacing = "2%",
  initialBottomOffset, // not used now
  maxWidth = "var(--width-350)",
  // Default: six images each with a scale of 1
  scaleArray = [1, 1, 1, 1, 1, 1],
  pulseConfig,
  transformPositionX = "50%",
  transformPositionY = "-50%",
  // Scroll props with defaults
  enableScroll = false,
  scrollThreshold = 5,
}) => {
  const imagesCount = scaleArray.length;
  const [visibleMessages, setVisibleMessages] = useState<number[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Track message heights dynamically
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [messageHeights, setMessageHeights] = useState<number[]>([]);

  // Smooth scroll offset
  const [scrollOffset, setScrollOffset] = useState(0);

  // Effect to capture message heights when they appear
  useEffect(() => {
    if (visibleMessages.length > 0) {
      const heights = messageRefs.current.map((ref) => ref?.offsetHeight || 0);
      setMessageHeights(heights);
    }
  }, [visibleMessages]);

  // Recalculate message heights on window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      const heights = messageRefs.current.map((ref) => ref?.offsetHeight || 0);
      setMessageHeights(heights);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add messages and calculate scroll with animation
  useEffect(() => {
    if (!startAnimation) {
      setVisibleMessages([]);
      setScrollOffset(0);
      setMessageHeights([]);
      return;
    }

    let timers: NodeJS.Timeout[] = [];

    for (let i = 0; i < imagesCount; i++) {
      const timer = setTimeout(() => {
        setVisibleMessages((prev) => {
          const newVisible = [...prev, i];
          return newVisible;
        });
      }, initialDelay + i * delayBetween);
      timers.push(timer);
    }

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [startAnimation, imagesCount, initialDelay, delayBetween]);

  // Calculate and apply scroll offset whenever visible messages or message heights change
  useEffect(() => {
    if (
      enableScroll &&
      visibleMessages.length > scrollThreshold &&
      containerRef.current
    ) {
      let totalOffset = 0;
      // Calculate the margin as a percentage of the container's height
      const containerHeight = containerRef.current.clientHeight;
      const spacingPercentage = parseFloat(verticalSpacing); // e.g., "2%" becomes 2
      const marginPx = containerHeight * (spacingPercentage / 100);

      // Sum heights of messages that should scroll out, adding the top and bottom margins
      const messagesToScroll = visibleMessages.length - scrollThreshold;
      for (let i = 0; i < messagesToScroll; i++) {
        const messageHeight = messageHeights[i] || 0;
        totalOffset += messageHeight + marginPx * 2;
      }

      // Apply smooth scrolling with animation
      setScrollOffset(totalOffset);
    }
  }, [
    visibleMessages,
    messageHeights,
    enableScroll,
    scrollThreshold,
    verticalSpacing,
  ]);

  // Configure pulse properties with defaults.
  const pulseEnabled = pulseConfig?.enabled;
  const pulseDuration = pulseConfig?.duration ?? 1.5;
  const pulseSize = pulseConfig?.size ?? 10; // maximum blur radius in px
  const pulseColor = pulseConfig?.color ?? "rgba(255, 255, 0, 0.5)";
  const minBlur = pulseSize / 2; // for a softer minimum glow

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        ...containerStyle,
      }}
    >
      {pulseEnabled && (
        <style>
          {`
          @keyframes pulseAnimation {
            0% { filter: drop-shadow(0 0 ${minBlur}px ${pulseColor}); }
            50% { filter: drop-shadow(0 0 ${pulseSize}px ${pulseColor}); }
            100% { filter: drop-shadow(0 0 ${minBlur}px ${pulseColor}); }
          }
        `}
        </style>
      )}
      <div
        style={{
          position: "absolute",
          width: chatWidth,
          height: "100%",
          left: "50%",
          transform: `translate(${transformPositionX},${transformPositionY})`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          paddingBottom: "5%",
          paddingTop: "20%", // Extra padding for scrolled-out messages
          overflow: "visible",
        }}
      >
        {/* Messages container that will scroll */}
        <div
          ref={messagesContainerRef}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            width: "100%",
            transform: `translateY(-${scrollOffset}px)`,
            transition: "transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1.0)",
          }}
        >
          {Array.from({ length: imagesCount }).map((_, index) => {
            const isRight = index % 2 === 0;
            // Get the target scale for this image.
            const finalScale =
              scaleArray[index] !== undefined ? scaleArray[index] : 1;
            // Use a slightly reduced scale when hidden.
            const hiddenScale = finalScale * 0.8;
            const isVisible = visibleMessages.includes(index);

            // Calculate if message should fade out (when scrolled up beyond view)
            const isScrolledOut =
              enableScroll &&
              visibleMessages.length > scrollThreshold &&
              index < visibleMessages.length - scrollThreshold;

            // Calculate fade based on scroll position for smoother transitions
            let opacity = isVisible ? 1 : 0;
            if (isScrolledOut && enableScroll) {
              const scrolledIndex =
                index - (visibleMessages.length - scrollThreshold);
              opacity = Math.max(0.0, 1 - Math.abs(scrolledIndex) * 1);
            }

            return (
              <div
                key={index}
                ref={(el) => (messageRefs.current[index] = el)}
                style={{
                  position: "relative",
                  alignSelf: isRight ? "flex-end" : "flex-start",
                  opacity: opacity,
                  transform: `scale(${isVisible ? finalScale : hiddenScale})`,
                  transition:
                    "opacity 1s ease-in-out, transform 0.8s cubic-bezier(0.18, 0.89, 0.32, 1.28)",
                  margin: verticalSpacing,
                  maxWidth: maxWidth,
                  zIndex: imagesCount - index,
                  transformOrigin: isRight ? "bottom right" : "bottom left",
                  pointerEvents: isScrolledOut ? "none" : "auto",
                }}
              >
                <img
                  src={`${baseImagePath}${index}.${imageExtension}`}
                  alt={`Chat message ${index + 1}`}
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "var(--radius, 12px)",
                    animation: pulseEnabled
                      ? `pulseAnimation ${pulseDuration}s ease-in-out infinite`
                      : undefined,
                  }}
                  onLoad={() => {
                    if (messageRefs.current[index]) {
                      const heights = messageRefs.current.map(
                        (ref) => ref?.offsetHeight || 0
                      );
                      setMessageHeights(heights);
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChatMessageAnimation;
